/**
 * @file Applies image upload functionaloties to an image. (specific markup needed)
 *
 * @author christian.sany@notch-interactive.com, diego.morales@notch-interactive.com
 *
 * @module component/image-upload
 */

import Vue from 'vue/dist/vue.min.js';
import overlay from '../components/overlay';
import observer from '../libs/observer';
import webcam from '../libs/webcam-capture';

const defaults = {
        filetypes: [
            'jpg',
            'jpeg',
            'png'
        ],
        maxFileSize: 2097152 // Equals to 2 MB
    },
    DEFAULT_CROP = 0,
    WEBCAM_CROP = 1;

/**
 * Get fileExtension of given fileName
 *
 * @param {string} fileName of the file
 * @return {string} fileextension
 */
const getFileExtension = (fileName) => {
    let temp = fileName.split('.');
    return temp[temp.length - 1];
};

// Exporting factory
export default (el) => {

    const instance = {
        id: el.id
    };

    // Private variables
    let cam,
        cropper,
        srcImageSize = [],
        cropBoxSize = [];

    // Private functions
    const initiateCam = (el) => {
        if (typeof cam !== 'object') {
            cam = webcam(el);
        }
    };

    const destroyCropper = () => {

        // Check if it has already been initiated
        if (typeof cropper === 'object') {

            // Destroy image cropping befor reinitiating
            cropper.destroy();
        }
    };

    /**
     * Get Image size of of source image.
     * This runs asynchronously.
     *
     * @param {string} imgUrl - Image url
     * @param {Function} callback - Function which runs after dimensions are set. Function is passed an array [width, height].
     */
    const getTrueSrcImageSize = (imgUrl, callback) => {
        let img = new Image();

        img.addEventListener('load', () => callback([img.width, img.height]));
        img.src = imgUrl;
    };

    const getCropBoxSize = (cropper) => {
        return [cropper.ui.holder[0].clientWidth, cropper.ui.holder[0].clientHeight];
    };

    const initCropper = (el, vm, callback, cropMode) => {
        let init = (imgDimensions) => {

            srcImageSize = imgDimensions;

            // Check if fixed aspect ratio exists
            if (vm.aspectRatio !== false) {

                let ratio = vm.aspectRatio.split(':');

                if (ratio.length !== 2) {
                    throw new Error('Given aspectRatio is not viable:', vm.aspectRatio);
                }

                // Init/Reinit cropper
                $(el).Jcrop({
                    minSize: [100, 100 * ratio[0] / ratio[1]],
                    setSelect: [0, 0, 100, 100 * ratio[0] / ratio[1]],
                    aspectRatio: ratio[0] / ratio[1],
                    onSelect: callback,
                    onChange: callback,
                    onRelease: function () {

                        // Don't allow deselecting the area
                        // This can't be prevented by an option, so we have to manually set it again in the release callback
                        this.setSelect([0, 0, 100, 100 * ratio[0] / ratio[1]]);
                    }
                }, function () {
                    cropper = this;
                    cropBoxSize = getCropBoxSize(cropper);

                    // Ensure that initial dimensions are set
                    cropper.setSelect([0, 0, 100, 100 * ratio[0] / ratio[1]]);
                });
            } else {

                // Init/Reinit cropper
                $(el).Jcrop({
                    onSelect: callback,
                    onChange: callback
                }, function () {
                    cropper = this;
                    cropBoxSize = getCropBoxSize(cropper);
                });
            }
        };

        switch (cropMode) {
            case DEFAULT_CROP:
                srcImageSize = getTrueSrcImageSize(vm.cropForm.image, init);
                break;

            case WEBCAM_CROP:
                srcImageSize = getTrueSrcImageSize(vm.webcamForm.image, init);
        }
    };

    const vm = new Vue({
        el: el,
        delimiters: ['[[', ']]'],

        // Default properties
        data: {
            loaded: false,
            id: el.id,
            aspectRatio: (el.getAttribute('data-iu-aspect-ratio')) ? el.getAttribute('data-iu-aspect-ratio') : false,
            thumbnail: (el.getAttribute('data-iu-thumbnail')) ? el.getAttribute('data-iu-thumbnail') : '',
            srcImage: (el.getAttribute('data-iu-src-image')) ? el.getAttribute('data-iu-src-image') : '',
            edit: el.getAttribute('data-iu-edit') === 'true',
            webcam: el.getAttribute('data-iu-webcam') === 'true',
            filetypes: (el.getAttribute('data-iu-filetypes')) ? JSON.parse(el.getAttribute('data-iu-filetypes')) : defaults.filetypes,
            maxFileSize: (el.getAttribute('data-iu-filesize')) ? el.getAttribute('data-iu-filesize') : defaults.maxFileSize,
            displayOptions: false,
            dragoverstate: false,
            imageBase64: '',
            errorMessage: '',
            webcamForm: {
                step: 1,
                image: '',
                width: 0,
                height: 0,
                x: '',
                y: ''
            },
            cropForm: {
                base64: '',
                image: '',
                width: 0,
                height: 0,
                x: '',
                y: ''
            }
        },

        // Computed properties
        computed: {
            webcamSaveDisable() {
                if (this.aspectRatio !== false && (this.webcamForm.width === 0 || this.webcamForm.height === 0)) {
                    return 'disabled';
                } else {
                    return false;
                }
            },
            cropSaveDisable() {
                if (this.aspectRatio !== false && (this.cropForm.width === 0 || this.cropForm.height === 0)) {
                    return 'disabled';
                } else {
                    return false;
                }
            }
        },
        methods: {

            /**
             * File validation
             *
             * @param {object} file to check
             * @return {boolean} if the validation is ok or not
             */
            fileValidation(file) {

                if (this.filetypes.indexOf(getFileExtension(file.name).toLowerCase()) < 0) {
                    this.errorMessage = 'filetype';

                    this.displayOptions = false;

                    return false;
                } else if (file.size > this.maxFileSize) {
                    this.errorMessage = 'filesize';

                    this.displayOptions = false;

                    return false;
                }

                // Reset errorMessage if no validation error occures
                this.errorMessage = '';

                return true;
            },

            /**
             * Handles changes to the file input field
             *
             * @param {event} e - event
             */
            inputChange(e) {
                if (e.target.files.length) {
                    if (this.fileValidation(e.target.files[0])) {
                        this.submitUpload(e.target.files[0]);
                    }
                }
            },

            dropzoneDragover(e) {
                e.stopPropagation();
                e.preventDefault();
            },

            dropzoneDragenter() {
                this.dragoverstate = true;
            },

            dropzoneDragleave() {
                this.dragoverstate = false;
            },

            dropzoneDrop(e) {
                e.stopPropagation();
                e.preventDefault();

                this.dragoverstate = false;

                // Only select first file (if multiple got dragged)
                if (this.fileValidation(e.dataTransfer.files[0])) {
                    let self = this;
                    let fr = new FileReader();
                    fr.onload = function (e2) {
                        self.imageBase64 = e2.target.result;
                        self.submitUpload(e2.target.result);
                    };
                    fr.readAsDataURL(e.dataTransfer.files[0]);
                }
            },

            submitUpload(file) {

                // When a fix aspect ratio is given, we land in edit dialogue befor sending the form
                if (this.aspectRatio !== false) {

                    // Already converted to base64 (got draggen)
                    if (typeof file === 'string') {

                        this.cropForm.image = file;
                        this.cropForm.base64 = file;
                        this.$refs.cropTrigger.click();

                    } else { // needs to be converted to base64 first

                        let self = this;
                        let fr = new FileReader();
                        fr.onload = function (e) {
                            self.cropForm.image = e.target.result;
                            self.cropForm.base64 = e.target.result;
                            self.$refs.cropTrigger.click();
                        };
                        fr.readAsDataURL(file);
                    }
                } else { // When no aspect ratio is given, automatically send form

                    // this.$refs.form.submit() doesn't trigger the wanted functionality
                    this.$refs.form.querySelector('[type="submit"]').click();
                }
            },

            uploadHandler(e) {

                // Hide options if shown
                this.displayOptions = false;
                this.errorMessage = '';

                // Event can be prevented in any of the subscribers
                instance.trigger('upload', e);
            },

            webcamHandler(e) {

                // Hide options if shown
                this.displayOptions = false;
                this.errorMessage = '';

                // Event can be prevented in any of the subscribers
                instance.trigger('webcam', e);
            },

            cropHandler(e) {

                // Hide options if shown
                this.displayOptions = false;
                this.errorMessage = '';

                // Event can be prevented in any of the subscribers
                instance.trigger('crop', e);
            },

            deleteHandler(e) {

                // Hide options if shown
                this.displayOptions = false;
                this.errorMessage = '';

                // Event can be prevented in any of the subscribers
                instance.trigger('delete', e);
            },

            webcamSnap() {
                const self = this;

                cam.snap((image) => {
                    self.webcamForm.image = image;
                    self.webcamForm.step = 2;
                });
            },

            coordsToWebcamCropForm(c) {
                if (cropBoxSize[0] && cropBoxSize[1]) {

                    // Probably not needed here to translate values, but still.. just to be sure.
                    this.webcamForm.width = (c.w / cropBoxSize[0]) * srcImageSize[0];
                    this.webcamForm.height = (c.h / cropBoxSize[1]) * srcImageSize[1];
                    this.webcamForm.x = (c.x / cropBoxSize[0]) * srcImageSize[0];
                    this.webcamForm.y = (c.y / cropBoxSize[1]) * srcImageSize[1];
                }
            },

            coordsToDefaultCropForm(c) {
                if (cropBoxSize[0] && cropBoxSize[1]) {
                    this.cropForm.width = (c.w / cropBoxSize[0]) * srcImageSize[0];
                    this.cropForm.height = (c.h / cropBoxSize[1]) * srcImageSize[1];
                    this.cropForm.x = (c.x / cropBoxSize[0]) * srcImageSize[0];
                    this.cropForm.y = (c.y / cropBoxSize[1]) * srcImageSize[1];
                }
            }
        },

        /**
         * Watches surtain data and performs actions when they change
         */
        watch: {

            // Track changes on the step
            'webcamForm.step'(newStep) {

                const self = this;

                if (newStep === 1 && this.edit) {
                    destroyCropper();
                }

                if (newStep === 2 && this.edit) {
                    setTimeout(function () {

                        // Destroy in case it is already initiated with a wrong source image
                        destroyCropper();

                        // Init/Reinit
                        initCropper(self.$refs.webcamCropImage, self, self.coordsToWebcamCropForm, WEBCAM_CROP);
                    }, 20);
                }
            },

            /**
             * Syncs srcImage with the default crop image
             *
             * The source image can only be set newly by the method instance.setImage() which is called from the outside
             * So we have to keep the default image for cropping in sync
             *
             * @param {string} newValue of the source image
             */
            srcImage(newValue) {
                this.cropForm.image = newValue;
            }
        },

        /**
         * created lifecycle hook
         */
        created() {

            // Check if a fixed aspectRatio exists, if so, edit will always be true
            if (this.aspectRatio !== false) {
                this.edit = true;
            }

            if (this.edit) {

                // Set the default for the cropOverlay
                this.cropForm.image = this.srcImage;
            }
        },

        /**
         * mounted lifecycle hook
         */
        mounted() {
            const self = this;

            // Give it some time, good things need time (finish rendering of image)
            setTimeout(function () {

                // Will display the component
                self.loaded = true;
            }, 500);

            // Init cropping overlay
            let cropOverlay = overlay({
                type: 'image-crop-overlay',
                overlayId: this.id,
                insertEndOfBody: true
            });

            // Add hooks when instance was created
            if (cropOverlay) {
                cropOverlay.on('opened', () => {
                    initCropper(self.$refs.defaultCropImage, self, self.coordsToDefaultCropForm, DEFAULT_CROP);
                });

                cropOverlay.on('closed', () => {
                    destroyCropper();

                    // Reset image
                    this.cropForm.image = this.srcImage;

                    // Reset base 64
                    this.cropForm.base64 = '';
                });
            }

            // Check webcam option
            if (this.webcam) {

                // Will only initiate, if not initiated yet
                initiateCam(self.$refs.webcamCapture);

                // Initiate webcam overlay
                let webcamOverlay = overlay({
                    type: 'image-webcam-overlay',
                    overlayId: this.id,
                    insertEndOfBody: true
                });

                // Add hooks when instance was created
                if (webcamOverlay) {
                    webcamOverlay.on('opened', () => {

                        // Set step to 1 (if not already so)
                        self.webcamForm.step = 1;
                        cam.startWebcam();
                    });

                    webcamOverlay.on('closed', () => {

                        self.webcamForm.step = 1;
                        cam.stopWebcam();
                    });
                }
            }

            // Init delete overlay
            overlay({
                type: 'image-delete-overlay',
                overlayId: this.id,
                insertEndOfBody: true
            });
        }
    });

    // Add composites
    Object.assign(instance, observer());

    // Public functions

    /**
     * Adds/Overwrites existing image(s)
     *
     * @param {object} images containing the thumbnail and the source image
     */
    instance.setImage = (images) => {
        if (typeof images === 'object' && images.hasOwnProperty('thumbnail') && images.hasOwnProperty('srcImage')) {

            // Set the new Image
            vm.thumbnail = images.thumbnail;
            vm.srcImage = images.srcImage;
        } else {
            throw new Error('Given images are not legitimate');
        }
    };

    /**
     * Removes the image from the instance
     */
    instance.removeImage = () => {

        // Removing image from vm
        vm.thumbnail = '';
        vm.srcImage = '';
    };

    return instance;
};
